/**
 * PhotoScannerPage — iter733 (Feb 2026)
 *
 * Dedicated public page that hosts the LandingNutritionLabelWidget
 * so users can bookmark and share the tool directly (nutramilo.com/tools/scanner).
 * Route: /tools/scanner.
 *
 * Structure = same top-nav + footer as the landing page, plus a
 * short hero explaining the tool, then the widget itself.
 */
import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { ArrowRight, ScanLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LandingNav, COPY } from "@/pages/LandingPage";
import LandingNutritionLabelWidget from "@/components/LandingNutritionLabelWidget";

export default function PhotoScannerPage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const t = COPY[language] || COPY.bg;

  return (
    <div className={isDark ? "bg-slate-950 min-h-screen" : "bg-white min-h-screen"} data-testid="photo-scanner-page">
      <Helmet>
        <title>{bg ? "AI Nutrition Facts Scanner · Nutramilo" : "AI Nutrition Facts Scanner · Nutramilo"}</title>
        <meta
          name="description"
          content={bg
            ? "Снимай хранителен етикет и виж DIL (диетичен инсулинов товар) и NIS % (Nutramilo Insulin Score) моментално. До 20 сканирания на ден, безплатно."
            : "Snap a nutrition label and see DIL (Dietary Insulin Load) and NIS % (Nutramilo Insulin Score) instantly. Up to 20 free scans per day."}
        />
        <link rel="canonical" href="https://nutramilo.com/tools/scanner" />
      </Helmet>

      <LandingNav t={t} />

      {/* Compact hero */}
      <section className={`relative overflow-hidden pt-10 pb-6 px-4 sm:px-6 lg:px-8 ${
        isDark ? "bg-gradient-to-b from-slate-950 via-sky-950/30 to-slate-950" : "bg-gradient-to-b from-white via-sky-50/40 to-white"
      }`}>
        <div className="max-w-4xl mx-auto text-center">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
            isDark ? "bg-sky-500/15 text-sky-300 border border-sky-500/30" : "bg-sky-100 text-sky-700 border border-sky-300"
          }`}>
            <ScanLine className="w-3 h-3" />
            {bg ? "Инструмент · Публичен" : "Tool · Public"}
          </div>
          <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-black mt-3 tracking-tight ${
            isDark ? "text-white" : "text-slate-900"
          }`}>
            {bg ? "AI " : "AI "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-purple-400 to-pink-400">
              Nutrition Facts Scanner
            </span>
          </h1>
          <p className={`mt-3 text-sm sm:text-base max-w-2xl mx-auto ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            {bg
              ? "Снимай хранителен етикет — AI извлича макросите и изчислява DIL & NIS. Комбинирай до 20 продукта на ден за пълен клиничен ефект."
              : "Snap a nutrition label — AI extracts macros and computes DIL & NIS. Combine up to 20 products per day for the full clinical impact."}
          </p>
        </div>
      </section>

      {/* The actual widget */}
      <LandingNutritionLabelWidget />

      {/* Cross-link back to Collagen tool + register */}
      <section className={`py-10 px-4 sm:px-6 lg:px-8 ${isDark ? "bg-slate-950" : "bg-white"}`}>
        <div className="max-w-3xl mx-auto text-center">
          <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {bg ? "Виж и другия ни безплатен инструмент:" : "See our other free tool:"}
          </p>
          <div className="mt-3 flex flex-col sm:flex-row justify-center gap-2">
            <Link to="/tools/collagen" data-testid="scanner-cross-collagen">
              <Button variant="outline" className={isDark ? "border-white/20 text-slate-200 hover:bg-white/10" : ""}>
                {bg ? "Колаген тип III · игра" : "Collagen Type-3 · game"}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/register" data-testid="scanner-cross-register">
              <Button className="gradient-primary text-white font-bold">
                {bg ? "Регистрация · пълен анализ" : "Register · full analysis"}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
